/**
 * features/hr/LeaveBalancesOverview.tsx
 * ------------------------------------------------------------------
 * HR LEAVE — a prominent, at-a-glance balances board across staff.
 * Annual + sick remaining per active staff member for the current
 * leave year, resolved through the cohort policy. Searchable; click a
 * row to open that staff member. Renders embedded in the Leave hub.
 * ------------------------------------------------------------------ */
import { useMemo, useState, Fragment } from 'react';
import { Search, CalendarDays, SlidersHorizontal, Check, X, Loader2 } from 'lucide-react';
import { SoftCard, StatChip, glassFieldClass } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import { useHrData } from '@/features/hr/useHrData';
import { useLeavePolicies } from '@/features/hr/useLeavePolicies';
import { useAuth } from '@/contexts/AuthContext';
import { hr } from '@/features/hr/hrWrite';
import { recordHrAudit } from '@/lib/hrAudit';
import { STAFF_CATEGORY_LABELS } from '@/lib/hr';
import { leaveBalancesFor, currentLeaveYear } from '@/lib/leavePolicy';
import { type LeaveBalance, type HrLeaveAllocation } from '@/lib/hrPayroll';
import { openingFromRemaining } from '@/lib/hrLeave';
import { salesFor } from '@/lib/leaveEncashment';
import LeaveCardPrint from '@/features/hr/LeaveCardPrint';

const LEAVE_LABEL = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

function remaining(balances: { type: string; remaining: number; entitlement: number; taken: number }[], type: string) {
  return balances.find((b) => b.type === type);
}

function bar(remainingDays: number, entitlement: number): { pct: number; tone: string } {
  const pct = entitlement > 0 ? Math.max(0, Math.min(100, Math.round((remainingDays / entitlement) * 100))) : 0;
  const tone = remainingDays <= 0 ? 'bg-red-500' : pct < 25 ? 'bg-amber-500' : 'bg-emerald-500';
  return { pct, tone };
}

export default function LeaveBalancesOverview({ onOpenStaff, canEdit = false }: { onOpenStaff?: (id: string) => void; canEdit?: boolean }) {
  const data = useHrData();
  const { policies } = useLeavePolicies();
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const year = currentLeaveYear();
  const accrualStart = data.payrollSettings?.leaveAccrualStart;

  const rows = useMemo(() => {
    return data.staff
      .filter((s) => s.status !== 'exited')
      .map((s) => {
        const allocation = data.leaveAllocations.find((a) => a.staffId === s.id);
        const requests = data.leave.filter((l) => l.staffId === s.id);
        const balances = leaveBalancesFor({ category: s.category, allocation, requests, policies, year, accrualStart, employmentStart: s.startDate, encashments: salesFor(data.leaveEncashments, s.id) });
        return { staff: s, balances, allocation };
      })
      .sort((a, b) => `${a.staff.lastName} ${a.staff.firstName}`.localeCompare(`${b.staff.lastName} ${b.staff.firstName}`));
  }, [data.staff, data.leaveAllocations, data.leave, policies, year]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.staff.firstName} ${r.staff.lastName} ${r.staff.employeeNo}`.toLowerCase().includes(q));
  }, [rows, search]);

  const summary = useMemo(() => {
    let totalAnnual = 0, low = 0;
    for (const r of rows) {
      const a = remaining(r.balances, 'annual');
      if (a) { totalAnnual += a.remaining; if (a.remaining <= 5) low += 1; }
    }
    const avg = rows.length ? Math.round((totalAnnual / rows.length) * 10) / 10 : 0;
    return { staff: rows.length, avgAnnual: avg, low };
  }, [rows]);

  if (data.loading) return <div className="flex justify-center py-16"><Spinner label="Loading balances…" /></div>;

  function openEditor(staffId: string, balances: LeaveBalance[]) {
    const d: Record<string, string> = {};
    for (const b of balances) d[b.type] = String(b.remaining);
    setDraft(d);
    setErr('');
    setEditId(staffId);
  }

  async function saveBalances(staffId: string, balances: LeaveBalance[], allocation: HrLeaveAllocation | undefined) {
    setBusy(true);
    setErr('');
    try {
      // Invert the (accrual-aware) balance formula to find the opening to store.
      const empStart = data.staffById(staffId)?.startDate;
      const invOpts = { accrualStart, employmentStart: empStart, asOf: new Date() };
      const newOpenings: Record<string, number> = { ...(allocation?.openingBalances ?? {}) };
      for (const b of balances) {
        const raw = draft[b.type];
        if (raw === undefined || raw === '') continue;
        const desired = Number(raw);
        if (!Number.isFinite(desired)) continue;
        newOpenings[b.type] = openingFromRemaining(b.type, desired, { entitlement: b.entitlement, taken: b.taken, opts: invOpts });
      }
      if (allocation) await hr.leaveAllocations.update(staffId, { openingBalances: newOpenings });
      else await hr.leaveAllocations.set(staffId, { staffId, openingBalances: newOpenings });
      await recordHrAudit({
        action: 'leave.balance.adjust',
        actor,
        subjectId: staffId,
        before: { openingBalances: allocation?.openingBalances ?? {} },
        after: { openingBalances: newOpenings },
      });
      setEditId(null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatChip tone="blue" value={String(summary.staff)} label="active staff" />
        <StatChip tone="emerald" value={`${summary.avgAnnual} d`} label="avg annual remaining" />
        <StatChip tone={summary.low > 0 ? 'red' : 'slate'} value={String(summary.low)} label="low on annual (≤5d)" />
      </div>

      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
            <CalendarDays size={16} /> Balances · leave year {year}
            <span className="ml-2 inline-flex gap-2 font-normal">
              <LeaveCardPrint staff={rows.filter((r) => r.staff.category === 'teaching').map((r) => r.staff)} label="Teachers' leave cards" />
              <LeaveCardPrint staff={rows.map((r) => r.staff)} label="All leave cards" />
            </span>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff…" className={glassFieldClass('py-1.5 text-sm pl-8')} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-2 px-4 font-medium">Staff</th>
                <th className="py-2 px-4 font-medium">Cohort</th>
                <th className="py-2 px-4 font-medium w-48">Annual remaining</th>
                <th className="py-2 px-4 font-medium text-right">Sick remaining</th>
                {canEdit && <th className="py-2 px-4 font-medium text-right w-24" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {filtered.map(({ staff, balances, allocation }) => {
                const annual = remaining(balances, 'annual');
                const sick = remaining(balances, 'sick');
                const b = annual ? bar(annual.remaining, annual.entitlement) : { pct: 0, tone: 'bg-gray-300' };
                return (
                  <Fragment key={staff.id}>
                  <tr
                    className={onOpenStaff ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40' : ''}
                    onClick={onOpenStaff ? () => onOpenStaff(staff.id) : undefined}
                  >
                    <td className="py-2.5 px-4">
                      <div className="font-medium text-gray-800 dark:text-gray-100">{staff.firstName} {staff.lastName}</div>
                      <div className="text-xs text-gray-400">#{staff.employeeNo}</div>
                    </td>
                    <td className="py-2.5 px-4 text-gray-600 dark:text-gray-300">{STAFF_CATEGORY_LABELS[staff.category]}</td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-2">
                        <span className={`text-lg font-bold tabular-nums ${annual && annual.remaining <= 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                          {annual ? annual.remaining : '—'}
                        </span>
                        <span className="text-xs text-gray-400">/ {annual?.entitlement ?? 0} d</span>
                      </div>
                      <div className="mt-1 h-1.5 w-40 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <div className={`h-full ${b.tone}`} style={{ width: `${b.pct}%` }} />
                      </div>
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <span className="font-semibold text-gray-800 dark:text-gray-100">{sick ? sick.remaining : '—'}</span>
                      <span className="text-xs text-gray-400"> / {sick?.entitlement ?? 0} d</span>
                    </td>
                    {canEdit && (
                      <td className="py-2.5 px-4 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); editId === staff.id ? setEditId(null) : openEditor(staff.id, balances); }}
                          className="inline-flex items-center gap-1 text-xs font-medium text-[var(--accent-link,#2d5295)] hover:underline"
                        >
                          <SlidersHorizontal size={13} /> Adjust
                        </button>
                      </td>
                    )}
                  </tr>
                  {canEdit && editId === staff.id && (
                    <tr className="bg-gray-50/70 dark:bg-gray-800/40">
                      <td colSpan={5} className="px-4 py-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          Set the correct <strong>remaining</strong> days for {staff.firstName} {staff.lastName} (go-live correction).
                          This adjusts the carried-in opening balance; entitlement and days already taken are shown for reference.
                        </p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {balances.map((bal) => (
                            <div key={bal.type} className="flex items-center gap-2 text-sm">
                              <span className="w-28 text-gray-700 dark:text-gray-200">{LEAVE_LABEL(bal.type)}</span>
                              <span className="text-[11px] text-gray-400 w-32">entitle {bal.entitlement} · taken {bal.taken}</span>
                              <input
                                type="number"
                                step="0.5"
                                value={draft[bal.type] ?? ''}
                                onChange={(e) => setDraft((d) => ({ ...d, [bal.type]: e.target.value }))}
                                onClick={(e) => e.stopPropagation()}
                                className={glassFieldClass('py-1 text-sm w-24')}
                              />
                              <span className="text-[11px] text-gray-400">days left</span>
                            </div>
                          ))}
                        </div>
                        {err && <p className="text-red-600 dark:text-red-400 text-sm mt-2">{err}</p>}
                        <div className="flex items-center gap-2 mt-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); saveBalances(staff.id, balances, allocation); }}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-[#1b3a6b] hover:bg-[#142c52] disabled:opacity-50"
                          >
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditId(null); }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            <X size={14} /> Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </SoftCard>
    </div>
  );
}
